import DataTable, {Order} from "datatables.net-bs5";
import {AjaxData} from "datatables.net";
import * as Highcharts from "highcharts";
import {filter_choices, get, post, notifier, number_format, on, on_keyup} from "../util";
import { getChartTheme } from "../utils/chartTheme";

declare const eo_version : string;
// Shared contract (217-06): emitted only inside {if $price_history} — absent from the DOM
// entirely when the item has no community trade price (D-08), so this is read defensively
// via `typeof eo_price_history !== 'undefined'` rather than assumed present.
declare const eo_price_history : {id: number, changed_at: number, value_min: number, value_max: number}[] | undefined;

interface ItemsAjaxData extends AjaxData {
    type : string
}

// D-10 guard flag: prevents a chart-level click handler from double-firing when a chart
// point click already handled the event (mirrors home.ts:258-261's _pointClickInProgress idiom).
let _pointClickInProgress = false;

export default class {
    static init = () => {
        items();
        sets();
        compare();
        trade_price_history();
    }
}

function items() {
    let table = document.querySelector('.datatable[data-table="eo_items"]') as HTMLElement;
    if(!table) {
        return;
    }

    let datatable = new DataTable('.datatable[data-table="eo_items"]',{
        pageLength: 20,
        lengthMenu: [10,20,50,100,500,1000,2000],
        processing: true,
        serverSide: true,
        responsive: true,
        ajax: {
            url: '?do=getItems',
            data: function (data: ItemsAjaxData, settings) {
                let type = document.querySelector('.js-eo-filter-type') as HTMLSelectElement;
                data.type = type.value;
                let search = settings.oPreviousSearch.search;
                let gold = document.querySelector('.js-total-eons') as HTMLElement;
                if(gold) {
                    get('?do=getTotalEons', {search: search}, (data: any) => {
                        if(data) {
                            gold.innerText = number_format(data,0);
                        }
                    });
                }
                return data;
            }
        },
        columns: [
            { data: 'id', className: "align-middle", width: "25px" },
            { data: null, className: "align-middle", width: "250px", render: function(data) {
                    // D-10: has_drops is a server-computed bool; badge markup is a static
                    // constant appended conditionally, never interpolated as data (WR-02).
                    let dropsBadge = data.has_drops ? ' <span class="badge text-bg-success">Drops</span>' : '';
                    return `<img src="/assets/eo/${eo_version}/items/${data.id}.png" class="eo-item-image-small" /> <a href="/eo/items/${data.id}">${data.name}</a>${dropsBadge}`;
                }},
            { data: null, className: "align-middle td-editable", render: function(data : any) {
                    return `<span class="js-trade-view" data-id="${data.id}" data-min="${data.trade_min}" data-max="${data.trade_max}">${data.trade}</span>`;
                } },
            { data: null, className: "align-middle td-editable", width: "110px", render: function(data : any) {
                    return `<span class="js-buy-view" data-id="${data.id}">${data.buy}</span>`;
                } },
            { data: 'value', className: "align-middle", width: "110px" },
            { data: null, className: "align-middle td-editable", width: "110px", render: function(data : any) {
                    return `<span class="js-witch-view" data-id="${data.id}">${data.value_witch}</span>`;
                } },
            { data: 'crafting', className: "align-middle", width: "160px" },
            { data: null, className: "align-middle td-editable", width: "110px", render: function(data : any) {
                    return `<span class="js-eo-amount-view" data-id="${data.id}">${data.amount}</span>`;
                } },
            { data: null, className: "align-middle", width: "110px", render: (data) => data.total === null ? '-' : number_format(data.total,0) },
        ],
        order: [[0,"asc"]] as Order[]
    });

    on('click','.js-witch-view',(target : HTMLElement) => {
        let td = (target?.parentElement as HTMLElement);

        let amount = target.innerText ?? "0";

        let input = document.createElement('input');
        input.type = 'number';
        input.step = '1';
        input.value = amount;
        input.classList.add('form-control', 'form-control-sm', 'js-witch-edit');
        input.setAttribute('data-id', target.getAttribute('data-id') ?? "0");

        td.innerHTML = '';
        td.appendChild(input);

        input.focus();
    },true);

    on('click','.js-buy-view',(target : HTMLElement) => {
        let td = (target?.parentElement as HTMLElement);

        let amount = target.innerText ?? "0";

        let input = document.createElement('input');
        input.type = 'number';
        input.step = '1';
        input.value = amount;
        input.classList.add('form-control', 'form-control-sm', 'js-buy-edit');
        input.setAttribute('data-id', target.getAttribute('data-id') ?? "0");

        td.innerHTML = '';
        td.appendChild(input);

        input.focus();
    },true)

    on('click','.js-eo-amount-view',(target : HTMLElement) => {
        let td = (target?.parentElement as HTMLElement);

        let amount = target.innerText ?? "0";

        let input = document.createElement('input');
        input.type = 'number';
        input.step = '1';
        input.value = amount;
        input.classList.add('form-control', 'form-control-sm', 'js-eo-amount-edit');
        input.setAttribute('data-id', target.getAttribute('data-id') ?? "0");

        td.innerHTML = '';
        td.appendChild(input);

        input.focus();
    },true);

    on('click','.js-trade-view',(target : HTMLElement) => {
        let td = (target?.parentElement as HTMLElement);

        let min = target.getAttribute('data-min') ?? "0";
        let max = target.getAttribute('data-max') ?? "0";

        let input_min = document.createElement('input');
        input_min.type = 'number';
        input_min.step = '1';
        input_min.value = min;
        input_min.classList.add('form-control', 'form-control-sm', 'js-trade-min-edit');
        input_min.setAttribute('data-id', target.getAttribute('data-id') ?? "0");

        let input_max = document.createElement('input');
        input_max.type = 'number';
        input_max.step = '1';
        input_max.value = max;
        input_max.classList.add('form-control', 'form-control-sm', 'js-trade-max-edit');
        input_max.setAttribute('data-id', target.getAttribute('data-id') ?? "0");

        let separator = document.createElement('span');
        separator.innerText = '-';
        separator.classList.add('input-group-text');

        let group = document.createElement('div');
        group.classList.add('input-group','w-100');
        group.appendChild(input_min);
        group.appendChild(separator);
        group.appendChild(input_max);

        td.innerHTML = '';
        td.appendChild(group);

        input_min.focus();
    },true);

    on('focusout','.js-witch-edit',(target : HTMLInputElement) => update_witch_from_table(target),true);
    on_keyup(13,'.js-witch-edit',(target : HTMLInputElement) => update_witch_from_table(target));

    on('focusout','.js-buy-edit',(target : HTMLInputElement) => update_buy_from_table(target),true);
    on_keyup(13,'.js-buy-edit',(target : HTMLInputElement) => update_buy_from_table(target));

    on('focusout','.js-eo-amount-edit',(target : HTMLInputElement) => update_amount_from_table(target),true);
    on_keyup(13,'.js-eo-amount-edit',(target : HTMLInputElement) => update_amount_from_table(target));

    on('focusout','.js-trade-min-edit',(target : HTMLInputElement) => update_sell_from_table(target),true);
    on_keyup(13,'.js-trade-min-edit',(target : HTMLInputElement) => update_sell_from_table(target));
    on('focusout','.js-trade-max-edit',(target : HTMLInputElement) => update_sell_from_table(target),true);
    on_keyup(13,'.js-trade-max-edit',(target : HTMLInputElement) => update_sell_from_table(target));

    function update_witch_from_table(input: HTMLInputElement) {
        let id = parseInt(input.getAttribute('data-id') ?? "0");
        let value = parseInt(input.value);
        get('?do=updateWitchValue', {item_id: id, value: value},(data : any) => {
            if(data.status == 1) {
                notifier.success("Witch value has been updated.");
            } else {
                notifier.alert(data.message);
            }
            datatable.ajax.reload(() => {},false);
        });
    }

    function update_amount_from_table(input: HTMLInputElement) {
        let id = parseInt(input.getAttribute('data-id') ?? "0");
        let value = parseInt(input.value);
        get('?do=updateAmount', {
            item_id: id,
            value: isNaN(value) ? 0 : value
        },(data : any) => {
            if(data.status == 1) {
                notifier.success("Amount has been updated.");
            } else {
                notifier.alert(data.message);
            }
            datatable.ajax.reload(() => {},false);
        });
    }

    function update_buy_from_table(input: HTMLInputElement) {
        let id = parseInt(input.getAttribute('data-id') ?? "0");
        let value = parseInt(input.value);
        get('?do=updateBuy', {item_id: id, value: value},(data : any) => {
            if(data.status == 1) {
                notifier.success("Buy price has been updated.");
            } else {
                notifier.alert(data.message);
            }
            datatable.ajax.reload(() => {},false);
        });
    }

    function update_sell_from_table(input: HTMLInputElement) {
        setTimeout(() => {
            if(document.activeElement !== input && (document.activeElement?.classList.contains('js-trade-min-edit') || document.activeElement?.classList.contains('js-trade-max-edit'))) {
                return;
            }
            let id = parseInt(input.getAttribute('data-id') ?? "0");
            let td = (input?.parentElement as HTMLElement);
            let min = parseInt((td.querySelector('.js-trade-min-edit') as HTMLInputElement).value);
            let max = parseInt((td.querySelector('.js-trade-max-edit') as HTMLInputElement).value);
            min = isNaN(min) ? 0 : min;
            max = isNaN(max) ? 0 : max;
            get('?do=updateSell', {item_id: id, min: min, max: max},(data : any) => {
                if(data.status == 1) {
                    notifier.success("Sell price has been updated.");
                } else {
                    notifier.alert(data.message);
                }
                datatable.ajax.reload(() => {},false);
            });
        }, 5);
    }

    on('change', '.js-eo-filter-type', (target: HTMLInputElement) => {
        datatable.ajax.reload(() => {},false);
    });
}

function sets() {
    let table = document.querySelector('.datatable[data-table="eo_sets"]') as HTMLElement;
    if(!table) {
        return;
    }

    let datatable = new DataTable('.datatable[data-table="eo_sets"]',{
        pageLength: 20,
        lengthMenu: [10,20,50,100],
        processing: true,
        serverSide: true,
        responsive: true,
        ajax: {
            url: '?do=getSets',
            data: function(data) {
                return data;
            }
        },
        columns: [
            { data: null, className: "align-middle", render: function(data) {
                    let html = '';
                    data.items.forEach((item_id : any) => {
                        html += `<a href="/eo/items/${item_id}"><img src="/assets/eo/${eo_version}/items/${item_id}.png" class="eo-item-image-small" /></a>`;
                    });
                    html += ` ${data.name}`;
                    return html;
                }},
            { data: null, className: "align-middle td-editable", render: function(data : any) {
                    return `<span class="js-trade-view" data-id="${data.id}" data-min="${data.trade_min}" data-max="${data.trade_max}">${data.trade}</span>`;
                } },
            { data: 'craft', className: "align-middle" },
        ],
        order: [[0,"asc"]] as Order[]
    });

    on('click','.js-trade-view',(target : HTMLElement) => {
        let td = (target?.parentElement as HTMLElement);

        let min = target.getAttribute('data-min') ?? "0";
        let max = target.getAttribute('data-max') ?? "0";

        let input_min = document.createElement('input');
        input_min.type = 'number';
        input_min.step = '1';
        input_min.value = min;
        input_min.classList.add('form-control', 'form-control-sm', 'js-trade-min-edit');
        input_min.setAttribute('data-id', target.getAttribute('data-id') ?? "0");

        let input_max = document.createElement('input');
        input_max.type = 'number';
        input_max.step = '1';
        input_max.value = max;
        input_max.classList.add('form-control', 'form-control-sm', 'js-trade-max-edit');
        input_max.setAttribute('data-id', target.getAttribute('data-id') ?? "0");

        let separator = document.createElement('span');
        separator.innerText = '-';
        separator.classList.add('input-group-text');

        let group = document.createElement('div');
        group.classList.add('input-group','w-100');
        group.appendChild(input_min);
        group.appendChild(separator);
        group.appendChild(input_max);

        td.innerHTML = '';
        td.appendChild(group);

        input_min.focus();
    },true);

    on('focusout','.js-trade-min-edit',(target : HTMLInputElement) => update_trade_from_table(target),true);
    on_keyup(13,'.js-trade-min-edit',(target : HTMLInputElement) => update_trade_from_table(target));
    on('focusout','.js-trade-max-edit',(target : HTMLInputElement) => update_trade_from_table(target),true);
    on_keyup(13,'.js-trade-max-edit',(target : HTMLInputElement) => update_trade_from_table(target));

    function update_trade_from_table(input: HTMLInputElement) {
        setTimeout(() => {
            if(document.activeElement !== input && (document.activeElement?.classList.contains('js-trade-min-edit') || document.activeElement?.classList.contains('js-trade-max-edit'))) {
                return;
            }
            let id = parseInt(input.getAttribute('data-id') ?? "0");
            let td = (input?.parentElement as HTMLElement);
            let min = parseInt((td.querySelector('.js-trade-min-edit') as HTMLInputElement).value);
            let max = parseInt((td.querySelector('.js-trade-max-edit') as HTMLInputElement).value);
            min = isNaN(min) ? 0 : min;
            max = isNaN(max) ? 0 : max;
            get('?do=updateSetTrade', {item_id: id, min: min, max: max},(data : any) => {
                if(data.status == 1) {
                    notifier.success("Trade price has been updated.");
                } else {
                    notifier.alert(data.message);
                }
                datatable.ajax.reload(() => {},false);
            });
        }, 5);
    }
}

function compare() {
    let compare1 = document.querySelector('.js-eo-compare-1') as HTMLSelectElement;
    let compare2 = document.querySelector('.js-eo-compare-2') as HTMLSelectElement;
    if(!compare1) {
        return;
    }

    interface CompareOption {
        value : string;
        label : string;
        type : string;
    }

    // Cache the full unfiltered option set (both dropdowns render the same list server-side) once, up front.
    let allCompareOptions : CompareOption[] = Array.from(compare1.options).map((option) => ({
        value: option.value,
        label: option.text,
        type: option.getAttribute('data-type') ?? ''
    }));

    // D-01/D-02: narrow targetSelect to items sharing sourceSelect's chosen eo_items_types_id.
    // "Please Select" (value 0) always stays available.
    function filterCompareDropdown(sourceSelect : HTMLSelectElement, targetSelect : HTMLSelectElement) {
        let chosen = allCompareOptions.find((option) => option.value === sourceSelect.value);
        let chosenType = chosen ? chosen.type : '';
        let filtered = allCompareOptions.filter((option) => option.value === '0' || !chosenType || option.type === chosenType);
        filter_choices(targetSelect, filtered.map((option) => ({value: option.value, label: option.label})));
    }

    // D-03: symmetric — either side changing narrows the other; most-recently-changed side is authoritative.
    compare1.addEventListener('change', () => filterCompareDropdown(compare1, compare2));
    compare2.addEventListener('change', () => filterCompareDropdown(compare2, compare1));

    // D-03: arriving via an item-page "Compare ➜" link (one side preset) filters the other dropdown on load.
    if(compare1.value && compare1.value !== '0') {
        filterCompareDropdown(compare1, compare2);
    } else if(compare2.value && compare2.value !== '0') {
        filterCompareDropdown(compare2, compare1);
    }

    compare1.addEventListener('change', compare_change);
    compare2.addEventListener('change', compare_change);

    function compare_change() {
        let id1 = compare1.value;
        let id2 = compare2.value;
        window.location.href = '/eo/compare/' + id1 + '/' + id2;
    }

    document.querySelectorAll('.btn-eo-compare-original').forEach((btn) => {
        btn.addEventListener('click', function() {
            btn.classList.remove('btn-outline-secondary');
            btn.classList.add('btn-secondary');
            document.querySelectorAll('.btn-eo-compare-calculated').forEach((el) => {
                el.classList.remove('btn-secondary');
                el.classList.add('btn-outline-secondary');
            });
            document.querySelectorAll('.table-eo-compare-calculated').forEach((el) => {
                el.classList.add('d-none');
            });
            document.querySelectorAll('.table-eo-compare').forEach((el) => {
                el.classList.remove('d-none');
            });
        });
    });

    document.querySelectorAll('.btn-eo-compare-calculated').forEach((btn) => {
        btn.addEventListener('click', function() {
            btn.classList.remove('btn-outline-secondary');
            btn.classList.add('btn-secondary');
            document.querySelectorAll('.btn-eo-compare-original').forEach((el) => {
                el.classList.remove('btn-secondary');
                el.classList.add('btn-outline-secondary');
            });
            document.querySelectorAll('.table-eo-compare').forEach((el) => {
                el.classList.add('d-none');
            });
            document.querySelectorAll('.table-eo-compare-calculated').forEach((el) => {
                el.classList.remove('d-none');
            });
        });
    });
}

// D-06/D-07/D-08/D-10: community-trade price-history stepped arearange chart + point-click
// delete. The `arearange` series type is registered globally via stardew.ts's unconditional
// highcharts extras side-effect import (single-entry rspack bundle) — no separate import
// needed here.
function trade_price_history() {
    const container = document.getElementById('chart_eo_trade_price_history');
    if (!container) {
        // D-08: the whole accordion section (and therefore this container) is absent from
        // the DOM when the item has no community trade price.
        return;
    }
    if (typeof eo_price_history === 'undefined' || !eo_price_history || eo_price_history.length === 0) {
        return;
    }

    const seriesData = eo_price_history.map((point) => ({
        // changed_at is already an integer epoch-ms value from getPriceHistory() (Blocker-2) —
        // used directly as the x value, never re-wrapped in new Date()/Date.parse().
        x: point.changed_at,
        low: point.value_min,
        high: point.value_max,
        id: String(point.id),
    }));

    let chart : Highcharts.Chart;
    // @ts-ignore
    chart = Highcharts.chart('chart_eo_trade_price_history', Highcharts.merge(getChartTheme(), {
        // V-02: deliberately no `chart.zooming` here — sidesteps the documented touch+zoom
        // point-click misfire on small viewports.
        title: {
            text: 'Community Trade Price',
            style: {
                fontSize: '14px'
            }
        },
        legend: {
            enabled: false
        },
        xAxis: {
            type: 'datetime',
            dateTimeLabelFormats: { hour: '%H:%M', minute: '%H:%M', day: '%e %b' },
            labels: {
                overflow: 'justify',
            }
        },
        yAxis: {
            title: {
                text: '',
            },
            minorGridLineWidth: 0,
            gridLineWidth: 0,
            allowDecimals: false
        },
        series: [{
            type: 'arearange',
            name: 'Trade price',
            step: 'left', // D-07: stepped band, no fake interpolation between real points
            data: seriesData,
            marker: { enabled: true, radius: 5 }, // V-02: explicit — reliable 320px touch target
            fillOpacity: 0.2, // dark-theme-safe subtle fill (repo precedent range 0.15-0.6)
        }],
        plotOptions: {
            series: {
                cursor: 'pointer',
                point: {
                    events: {
                        click: function () {
                            _pointClickInProgress = true;

                            // D-10: visible/actionable only when logged in — this client-side
                            // gate is defense-in-depth only, the server's requirePost()/
                            // allowSaving() (217-05) is the authoritative control.
                            if (container?.dataset.loggedIn !== '1') {
                                _pointClickInProgress = false;
                                return;
                            }
                            if (!confirm('Delete this price point?')) {
                                _pointClickInProgress = false;
                                return;
                            }

                            const point = this as any as Highcharts.Point;
                            post('?do=deleteTradePriceHistory', {history_id: (point.options as any).id}, (data: any) => {
                                if (data.status == 1) {
                                    notifier.success('Price history point deleted.');
                                    // Re-render: drop the deleted point from the chart, no full re-fetch needed.
                                    point.remove();
                                } else {
                                    notifier.alert(data.message);
                                }
                            });

                            setTimeout(() => { _pointClickInProgress = false; }, 0);
                        }
                    }
                }
            }
        },
        credits: {
            enabled: false
        }
    } as any));
}
